import React, { useState } from 'react';
import type { ConsentItem } from '../data/constants';
import BottomSheet from './bottom-sheet';

interface ConsentDetailsProps {
  item: ConsentItem;
}

const ConsentDetails: React.FC<ConsentDetailsProps> = ({ item }) => (
  <>
    {item.details}
    {item.feeInfoBox && (
      <div
        className="mt-3 p-4 rounded text-xs leading-relaxed text-black bg-brand bg-opacity-10"
      >
        <p className="font-semibold mb-1">{item.feeInfoBox.title}</p>
        <p className="mb-2">{item.feeInfoBox.intro}</p>
        <ul className="list-disc pl-5 mb-2 space-y-1">
          {item.feeInfoBox.bullets.map((b) => (
            <li key={b}>{b}</li>
          ))}
        </ul>
        <p>{item.feeInfoBox.footer}</p>
      </div>
    )}
    {item.link && (
      <a
        href={item.link}
        target="_blank"
        rel="noopener noreferrer"
        className="flex items-center gap-1 mt-2 font-semibold hover:underline text-brand"
      >
        {item.linkLabel}
        {' '}
        ↗
      </a>
    )}
  </>
);

interface ConsentCardProps {
  item: ConsentItem;
  accepted: boolean;
  onToggle: () => void;
}

const ConsentCard: React.FC<ConsentCardProps> = ({ item, accepted, onToggle }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [sheetOpen, setSheetOpen] = useState(false);

  const handleReadDetails = (event: React.MouseEvent) => {
    event.stopPropagation();
    setSheetOpen(true);
  };

  const handleCloseSheet = () => {
    setSheetOpen(false);
  };

  const handleDesktopToggleExpand = () => {
    setIsExpanded((isCurrentlyExpanded) => !isCurrentlyExpanded);
  };

  return (
    <div
      className={`relative overflow-hidden transition-all rounded-2lg border ${
        accepted
          ? 'border-brand bg-brand bg-opacity-5'
          : 'border-slate-200 bg-white'
      }`}
    >
      <button
        type="button"
        className="md:hidden absolute inset-0 z-portal-card-tap cursor-pointer"
        aria-label={`Accept ${item.title}`}
        onClick={onToggle}
      />

      <div className="flex gap-3 items-start p-4 pr-12 md:pr-4">
        <span className="text-2xl mt-0.5 flex-shrink-0">{item.icon}</span>
        <div className="flex-1 min-w-0">
          <p className="font-semibold text-slate-800 text-sm mb-1">{item.title}</p>
          <p className="text-xs text-slate-500 leading-relaxed">{item.summary}</p>
        </div>
        <span
          className={`md:hidden absolute top-4 right-4 w-6 h-6 rounded-lg flex items-center justify-center border flex-shrink-0 ${
            accepted ? 'border-brand bg-brand' : 'border-slate-300 bg-white'
          }`}
          aria-hidden
        >
          {accepted && <span className="text-white text-xs">✓</span>}
        </span>
      </div>

      <div className="px-4 pb-2 relative z-portal-card-action">
        <button
          type="button"
          onClick={handleReadDetails}
          className="md:hidden text-xs font-semibold flex items-center gap-1 text-brand min-h-yn-btn"
        >
          Read full details →
        </button>
        <button
          type="button"
          onClick={handleDesktopToggleExpand}
          className="hidden md:flex text-xs font-semibold items-center gap-1 text-brand"
        >
          {isExpanded ? 'Hide details ▲' : 'Read full details ▼'}
        </button>
      </div>

      {isExpanded && (
        <div
          className="hidden md:block mx-4 mb-3 p-3 border text-xs leading-relaxed text-slate-600 whitespace-pre-line rounded-lg-plus bg-slate-50 border-brand border-opacity-10"
        >
          <ConsentDetails item={item} />
        </div>
      )}

      <div className="hidden md:block px-4 pb-4 border-t border-slate-100 pt-3">
        <button
          type="button"
          onClick={onToggle}
          className={`flex items-center gap-2 text-sm font-semibold min-h-yn-btn ${
            accepted ? 'text-brand' : 'text-slate-500'
          }`}
        >
          <span
            className={`w-5 h-5 rounded-lg flex items-center justify-center transition-all border ${
              accepted ? 'border-brand bg-brand' : 'border-slate-300 bg-white'
            }`}
          >
            {accepted && <span className="text-white text-xs">✓</span>}
          </span>
          {accepted ? 'Accepted ✓' : 'I accept this'}
        </button>
      </div>

      <BottomSheet
        visible={sheetOpen}
        title={item.title}
        onClose={handleCloseSheet}
      >
        <div className="text-xs leading-relaxed text-slate-600 whitespace-pre-line">
          <ConsentDetails item={item} />
        </div>
      </BottomSheet>
    </div>
  );
};

export default ConsentCard;
